import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { useMemo, useState } from "react";
import { Link, useOutletContext, useSearchParams } from "react-router";
import type { NutritionProductDto } from "../../../shared/nutrition-addon";
import { api } from "../../api/client";
import { useMenus } from "../../api/queries";
import { Alert, ICON } from "../../components/ui";
import type { ShellContext } from "../AppShell";
import { RecipeBlock } from "../menus/RecipeBlock";

// "Configurează valorile nutriționale" (owner 2026-11-02): like the allergen wizard, one product per screen, only
// the products that still need the restaurant (an ingredient the AI could not find, or a recipe not checked yet).
// Each screen asks only what is unclear; the restaurant confirms, completes or skips and comes back later.

/** A product the restaurant still has to look at. */
const needsSetup = (p: NutritionProductDto) => p.missing.length > 0 || (p.aiUnverified && p.lines > 0);

export function NutritionSetup() {
  const { membership } = useOutletContext<ShellContext>();
  const rid = membership.restaurant.id;
  const canEdit = membership.role !== "staff";
  const qc = useQueryClient();
  const [params] = useSearchParams();
  const menus = useMenus(rid);
  const list = (menus.data ?? []).filter((m) => !m.archived);
  const [menuId, setMenuId] = useState<string | null>(params.get("menu"));
  const mid = menuId ?? list[0]?.id ?? null;
  const key = ["nutrition", rid, "menu", mid];
  const q = useQuery({
    queryKey: key,
    queryFn: () =>
      api<{ products: NutritionProductDto[] }>("GET", `/restaurants/${rid}/nutrition/menus/${mid}/products`),
    enabled: !!mid,
  });
  // The list is taken once per menu, so the order stays while the restaurant goes through it.
  const [round, setRound] = useState(0);
  // biome-ignore lint/correctness/useExhaustiveDependencies: a new round (or menu) takes the list again on purpose
  const items = useMemo(() => (q.data?.products ?? []).filter(needsSetup), [q.isSuccess, mid, round]);
  const [i, setI] = useState(0);
  const [done, setDone] = useState<Set<string>>(new Set());
  const item = items[i];
  const refresh = () => qc.invalidateQueries({ queryKey: ["nutrition", rid] });

  const finished = items.length > 0 && i >= items.length;
  const skipped = items.filter((p) => !done.has(p.id)).length;
  return (
    <>
      <div className="stack" style={{ gap: 6 }}>
        <Link to="/app/nutrition" className="caption">
          ← Valori nutriționale
        </Link>
        <h1 className="h1">Configurează valorile nutriționale</h1>
        <p className="page-sub" style={{ margin: 0, maxWidth: 680 }}>
          Trecem prin produsele la care AI-ul are nevoie de tine. La fiecare îți arătăm rețeta propusă și te
          întrebăm doar ce nu e clar. Confirmă, completează sau sari peste și revino mai târziu.
        </p>
      </div>
      {list.length > 1 && (
        <div className="field" style={{ maxWidth: 360 }}>
          <label className="field-label" htmlFor="ns-menu">
            Meniul
          </label>
          <select
            id="ns-menu"
            className="field-input"
            value={mid ?? ""}
            onChange={(e) => {
              setMenuId(e.target.value);
              setI(0);
              setDone(new Set());
            }}
          >
            {list.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </select>
        </div>
      )}
      {q.isError && <Alert tone="error">{q.error.message}</Alert>}
      {q.isSuccess && items.length === 0 && (
        <section className="card" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <Alert tone="success" title="Nimic de lămurit">
            Toate produsele analizate au rețeta completă și verificată. Produsele fără rețetă se analizează
            din pagina Valori nutriționale.
          </Alert>
          <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
            <Link to="/app/nutrition/alergeni" className="btn btn-primary">
              Confirmă alergenii
            </Link>
            <Link to="/app/nutrition" className="btn btn-secondary">
              Înapoi la Valori nutriționale
            </Link>
          </div>
        </section>
      )}
      {finished && (
        <section className="card" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <Alert
            tone={skipped ? "warning" : "success"}
            title={skipped ? `${skipped} produse sărite` : "Gata"}
          >
            {skipped
              ? "Le poți relua acum sau mai târziu: rămân în listă până le confirmi."
              : "Toate produsele au rețeta confirmată. Valorile apar pe meniu ca „valori estimate”."}
          </Alert>
          <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
            {skipped > 0 && (
              <button
                type="button"
                className="btn btn-secondary"
                onClick={async () => {
                  await refresh();
                  setDone(new Set());
                  setRound((n) => n + 1);
                  setI(0);
                }}
              >
                Reia produsele sărite
              </button>
            )}
            <Link to="/app/nutrition/alergeni" className="btn btn-primary">
              Confirmă alergenii
            </Link>
            <Link to="/app/nutrition" className="btn btn-ghost">
              Înapoi la Valori nutriționale
            </Link>
          </div>
        </section>
      )}
      {item && (
        <section
          className="card"
          aria-labelledby="ns-h"
          style={{ display: "flex", flexDirection: "column", gap: 10 }}
        >
          <span className="muted caption" aria-live="polite">
            Produsul {i + 1} din {items.length} · {item.categoryName}
            {item.portion ? ` · ${item.portion}` : ""}
          </span>
          <h2 id="ns-h" className="h2" style={{ margin: 0 }}>
            {item.name}
          </h2>
          <p className="caption muted" style={{ margin: 0 }}>
            {item.missing.length
              ? "Rețeta propusă de AI. Lămurește ingredientele de mai jos, verifică gramajele și confirmă."
              : "Rețeta propusă de AI. Verifică ingredientele și gramajele și confirmă."}
          </p>
          {canEdit ? (
            <RecipeBlock
              key={item.id}
              rid={rid}
              mid={mid ?? ""}
              pid={item.id}
              compact
              shareMissing
              saveLabel="Confirm rețeta"
              onBaseSaved={() => void refresh()}
              onSaved={() => {
                setDone((d) => new Set(d).add(item.id));
                void refresh();
                setI((n) => n + 1);
              }}
            />
          ) : (
            <Alert tone="info">Doar proprietarul sau managerul pot confirma rețetele.</Alert>
          )}
          <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
            <button
              type="button"
              className="btn btn-secondary"
              disabled={i === 0}
              onClick={() => setI(i - 1)}
            >
              <ArrowLeft {...ICON} aria-hidden="true" /> Înapoi
            </button>
            <button type="button" className="btn btn-ghost" onClick={() => setI(i + 1)}>
              Sari, revin mai târziu <ArrowRight {...ICON} aria-hidden="true" />
            </button>
          </div>
        </section>
      )}
    </>
  );
}
